import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Api from "../../config/Api";
import Logo from "../../assets/images/logo.png";
import StatusBadge from "../SDRFManagement/Application/Components/StatusBadge";
import ReadMore from "../Components/Widget/ReadMore";
import dayjs from "dayjs";

const TrackingApplication = () => {
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState(null);
  const [applicationData, setApplicationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  // Step 1: Request OTP
  const handleRequestOtp = async (data) => {
    setIsLoading(true);
    try {
      const body = { applicationId: data.applicationId, phone: data.phone };
      const res = await Api("send-tracking-otp", "POST", body);

      if (res.data.success) {
        toast.success("OTP sent successfully", { position: toast.POSITION.BOTTOM_RIGHT });
        setStep(2);
      } else {
        toast.error(res.data.error || "Failed to send OTP", { position: toast.POSITION.BOTTOM_RIGHT });
      }
    } catch (err) {
      toast.error("Server error. Please try again!", { position: toast.POSITION.BOTTOM_RIGHT });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (data) => {
    setIsLoading(true);
    try {
      const body = { applicationId: data.applicationId, phone: data.phone, otp: data.otp };
      const res = await Api("verify-tracking-otp", "POST", body);

      if (res.data.success) {
        setApplicationData(res.data.application_details);
        setStatus(res.data.application_details.status);
        setStep(3);
        toast.success("OTP Verified", { position: toast.POSITION.BOTTOM_RIGHT });
      } else {
        toast.error(res.data.error || "Invalid OTP", { position: toast.POSITION.BOTTOM_RIGHT });
      }
    } catch (err) {
      toast.error("Server error. Please try again!", { position: toast.POSITION.BOTTOM_RIGHT });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      className="loginpage"
      style={{
        minHeight: "100vh",
        paddingTop: "80px",
        paddingBottom: "40px",
        background: "#f8f9fa",
      }}
    >
      <div className="container">
        <div className="row justify-content-center">
          {/* Steps 1 & 2: Centered Box */}
          {(step === 1 || step === 2) && (
            <div className="col-xl-5 col-lg-6 col-md-8 col-sm-10">
              <div className="homeloginbox p-4 shadow-sm bg-white rounded-3">
                <div className="homelogo text-center mb-3">
                  <Link to="/">
                    <img src={Logo} alt="logo" />
                  </Link>
                </div>
                <h5 className="text-center mb-4 text-dark">Track Application</h5>

                {/* Step 1: ID + Phone */}
                {step === 1 && (
                  <form onSubmit={handleSubmit(handleRequestOtp)}>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Application ID"
                        disabled={isLoading}
                        {...register("applicationId", { required: "Application ID is required" })}
                      />
                      {errors.applicationId && <small className="text-danger">{errors.applicationId.message}</small>}
                    </div>

                    <div className="mb-3">
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="Phone Number"
                        disabled={isLoading}
                        {...register("phone", { required: "Phone number is required" })}
                      />
                      {errors.phone && <small className="text-danger">{errors.phone.message}</small>}
                    </div>

                    <div className="d-grid">
                      <button type="submit" className="btn btn-primary" disabled={isLoading}>
                        {isLoading ? "Sending OTP..." : "Send OTP"}
                      </button>
                    </div>
                  </form>
                )}

                {/* Step 2: OTP */}
                {step === 2 && (
                  <form onSubmit={handleSubmit(handleVerifyOtp)}>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter OTP"
                        disabled={isLoading}
                        {...register("otp", { required: "OTP is required" })}
                      />
                      {errors.otp && <small className="text-danger">{errors.otp.message}</small>}
                    </div>

                    <div className="d-grid">
                      <button type="submit" className="btn btn-success" disabled={isLoading}>
                        {isLoading ? "Verifying..." : "Verify OTP"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Full-width Table */}
          {step === 3 && (
            <div className="col-12">
              <h5 className="text-center mb-4 text-dark">Application Status</h5>
              <div className="shadow-sm rounded-3 bg-white p-3 mb-3">
                  <div className="row text-center text-md-start">
                    <div className="col-md-4 mb-2 mb-md-0">
                      <strong>Application No.</strong> {applicationData?.application_id || "-"}
                    </div>
                    <div className="col-md-4 mb-2 mb-md-0">
                      <strong>Applicant Name:</strong> {applicationData?.applicant_name || "-"}
                    </div>
                    <div className="col-md-4">
                      <strong>Applicant Phone:</strong> {applicationData?.mobile || "-"}
                    </div>
                  </div>
              </div>
              <div
                className="table-responsive shadow-sm rounded-3 bg-white p-3"
                style={{ maxHeight: "70vh", overflowY: "auto", border: "1px solid #dee2e6" }}
              >
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Designation</th>
                      <th style={{ width: "30%" }}>Remark</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applicationData?.application_status_log?.length ? (
                      applicationData.application_status_log.map((item, i) => (
                        <tr key={i}>
                          <td className="fw-semibold">{i + 1}</td>
                          <td className="text-truncate" style={{ maxWidth: 120 }}>
                            {item.name}
                          </td>
                          <td className="text-truncate" style={{ maxWidth: 120 }}>
                          {item.user?.designation
                            ? item.user.designation
                            : item.center?.center_name
                            ? item.center.center_name
                            : "-"}
                          </td>
                          <td className="text-wrap" style={{ maxWidth: 250 }}>
                            {item.remark ? <ReadMore>{item.remark}</ReadMore> : "-"}
                          </td>
                          <td>
                            <StatusBadge status={item.status} />
                          </td>
                          <td className="text-nowrap">
                            {dayjs(item.created_at).format("DD MMM, YYYY - hh:mm a")}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          No status records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="text-center mt-4">
                <button
                  className="btn btn-outline-primary w-100"
                  onClick={() => {
                    setStep(1);
                    setStatus(null);
                    setApplicationData(null);
                    reset();
                  }}
                >
                  Track Another Application
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TrackingApplication;
